import { useNavigate } from "@tanstack/react-router";
import { Bell, LogOut, Moon, Plus, Search, Settings, Sun, UserRound } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type TopBarProps = {
  onOpenCommand: () => void;
};

export function TopBar({ onOpenCommand }: TopBarProps) {
  const navigate = useNavigate();
  const { profile, user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const displayName = profile ? `${profile.fname} ${profile.lname}`.trim() : user?.email;
  const initials =
    profile?.fname && profile?.lname
      ? `${profile.fname[0]}${profile.lname[0]}`.toUpperCase()
      : (user?.email?.[0] ?? "U").toUpperCase();

  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b border-border bg-card px-3">
      <button
        type="button"
        onClick={onOpenCommand}
        className="mx-auto flex h-8 w-full max-w-xl items-center gap-2 rounded-md border border-border bg-background px-3 text-xs text-muted-foreground transition-colors hover:border-border-strong hover:text-foreground"
      >
        <Search className="size-3.5" />
        <span className="flex-1 text-left">Jump or create… (Ctrl+K)</span>
        <kbd className="pointer-events-none hidden rounded border border-border bg-muted px-1.5 font-mono text-[10px] sm:inline">
          Ctrl K
        </kbd>
      </button>

      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          size="icon-sm"
          className="size-8"
          aria-label="New client"
          onClick={() => void navigate({ to: "/clients/new" })}
        >
          <Plus className="size-4" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Settings"
          onClick={() => void navigate({ to: "/settings" })}
        >
          <Settings />
        </Button>
        <Button type="button" variant="ghost" size="icon-sm" aria-label="Notifications" disabled>
          <Bell />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
        >
          {theme === "dark" ? <Sun /> : <Moon />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon-sm" className="ml-0.5">
              <Avatar className="size-6">
                <AvatarFallback className="bg-primary/10 text-[10px] font-semibold text-primary">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="font-normal">
              <p className="text-xs font-medium">{profile ? displayName : "User"}</p>
              <p className="truncate text-2xs text-muted-foreground">{user?.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <UserRound className="size-3.5" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void signOut()}>
              <LogOut className="size-3.5" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
